// What the game chart lays over its line: update markers, sale bands and
// evening bands, placed in time (unix seconds) across the charted span, from
// its first point's t0 to its last's t1. chart-draw.ts maps them to pixels
// and SVG; apart from it, so they run (and are tested,
// ../chart-overlays.test.ts) without a browser.

import type { Resolution, SaleMark, UpdateMark } from "../../../shared/api.ts";
import { EVENINGS, EVENING_HOURS, REGIONS, type Region, zonedHour } from "../../../shared/evenings.ts";
import { bucketOf, DAY, nearestByTime } from "./chart.ts";
import { UPDATE_KINDS } from "../updates.ts";

// A game's updates as the chart places them: `marks`, the shown ones by the
// start of the hour/day they fall in, each bucket one marker; `byPoint`, the
// same by the point whose tooltip lists them (the nearest, its own unless
// the crawler missed that bucket); `hidden`, each hidden kind's buckets,
// for its preview markers; and `counts`, how many of each kind fall in the
// span, shown or not, for the toggles.
interface UpdatePlaces {
  marks: Map<number, UpdateMark[]>;
  byPoint: Map<number, UpdateMark[]>;
  hidden: Map<string, number[]>;
  counts: Record<string, number>;
}

// Adds `value` to the list `map` keeps under `key`.
function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

// `updates` placed on a chart of `points` (sorted by t) at `resolution`,
// where the kinds in `shown` are marked. Updates outside the charted span,
// or of a kind the site doesn't know, are left off and not counted.
export function placeUpdates(
  updates: readonly UpdateMark[],
  points: readonly { t: number }[],
  resolution: Resolution,
  shown: ReadonlySet<string>
): UpdatePlaces {
  const places: UpdatePlaces = {
    marks: new Map(),
    byPoint: new Map(),
    hidden: new Map(),
    counts: Object.fromEntries(UPDATE_KINDS.map((k) => [k, 0]))
  };
  const { marks, byPoint, hidden, counts } = places;
  // With no points, nothing is in the span.
  const t0 = points[0]?.t ?? Infinity;
  const t1 = points.at(-1)?.t ?? -Infinity;

  for (const update of updates) {
    const t = bucketOf(update.t, resolution);
    if (!Object.hasOwn(counts, update.kind) || t < t0 || t > t1) continue;

    counts[update.kind] = (counts[update.kind] ?? 0) + 1;
    if (!shown.has(update.kind)) {
      // One preview marker per bucket, however many updates share it.
      const ts = hidden.get(update.kind);
      if (!ts?.includes(t)) push(hidden, update.kind, t);
      continue;
    }

    push(marks, t, update);
    const nearest = nearestByTime(points, t);
    if (nearest) push(byPoint, nearest.t, update);
  }

  return places;
}

// Whether `sale` is on at some point from `from` to the end of the bucket
// starting at `to`, `unit` long.
const isOn = (sale: SaleMark, from: number, to: number, unit: number): boolean =>
  sale.t < to + unit && (sale.end === null || sale.end > from);

// The sales on at some point in the span from `t0` to the end of `t1`'s
// bucket, `unit` long, in their order. Older API answers (cached) have no
// `sales`.
export const salesIn = (sales: readonly SaleMark[] = [], t0: number, t1: number, unit: number): SaleMark[] =>
  sales.filter((sale) => isOn(sale, t0, t1, unit));

// The sale on during the bucket starting at `t`, `unit` long, if any (the
// latest, should two meet in it).
export const saleAt = (sales: readonly SaleMark[], t: number, unit: number): SaleMark | undefined =>
  sales.findLast((sale) => isOn(sale, t, t, unit));

// A band across the chart, `from` to `to`.
interface Span {
  from: number;
  to: number;
}

// Each sale's band, from the hour its discount was first seen to the hour it
// was gone, cut to the span from `t0` to `t1`; a sale starting or ending
// partway through a bucket starts or ends partway through it here too. A
// sale still on runs to the last point, and one that began after it (in the
// last day or week, or an hour not published yet) is empty at `t1`, for the
// chart to draw as a sliver ending there.
export function saleSpans(sales: readonly SaleMark[], t0: number, t1: number): (Span & { sale: SaleMark })[] {
  const within = (t: number): number => Math.min(Math.max(t, t0), t1);
  return sales.map((sale) => ({ sale, from: within(sale.t), to: within(sale.end ?? t1) }));
}

// The evenings of each region in `regions` within the span from `t0` to
// `t1`, in that region's own time (so they follow its daylight saving), in
// REGIONS order and then by day. An evening that started the UTC day before
// t0 is cut to start at t0.
export function eveningSpans(
  regions: ReadonlySet<string>,
  t0: number,
  t1: number
): (Span & { region: Region; label: string })[] {
  const spans: (Span & { region: Region; label: string })[] = [];

  for (const region of REGIONS) {
    if (!regions.has(region)) continue;

    const { label, zone, from } = EVENINGS[region];

    for (let day = bucketOf(t0, "day") - DAY; day <= t1; day += DAY) {
      const start = Math.max(zonedHour(zone, day, from), t0);
      const end = Math.min(zonedHour(zone, day, from + EVENING_HOURS), t1);
      if (end > start) spans.push({ region, label, from: start, to: end });
    }
  }

  return spans;
}
